import type { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { ApiModel, ApiOptionalRelationship, ApiResource } from 'lib/interfaces/api';
import type { StepPreview } from 'lib/interfaces/StepsConfig';

// When adding to this list, please also ensure they are added to CreateInquiryTemplateModal if required.
export enum BuilderName {
  Aamva = 'aamva',
  Database = 'database',
  DatabaseOrGovernmentId = 'database_or_government_id',
  DatabaseOrGovernmentIdAndSelfie = 'database_or_government_id_and_selfie',
  DatabasePhoneCarrier = 'database_phone_carrier',
  DatabaseWithSsn = 'database_with_ssn',
  Document = 'document',
  EmailAddress = 'email_address',
  GovernmentId = 'government_id',
  GovernmentIdAndDatabase = 'government_id_and_database',
  GovernmentIdAndDatabaseAndSelfie = 'government_id_and_database_and_selfie',
  GovernmentIdAndDocument = 'government_id_and_document',
  GovernmentIdAndFormAndSelfie = 'government_id_and_form_and_selfie',
  GovernmentIdAndProofOfAddress = 'government_id_and_document_proof_of_address',
  GovernmentIdAndSelfie = 'government_id_and_selfie',
  GovernmentIdAndSelfieAndDocument = 'government_id_and_selfie_and_document',
  GovernmentIdAndSelfieAndProofOfAddress = 'government_id_and_selfie_and_document_proof_of_address',
  GovernmentIdFrontAndBack = 'government_id_front_and_back',
  GovernmentIdWithAutoclassification = 'government_id_with_autoclassification',
  GovernmentIdWithAutoclassificationAndSelfie = 'government_id_with_autoclassification_and_selfie',
  HealthInsuranceCard = 'health_insurance_card',
  PhoneNumber = 'phone_number',
  PhoneNumberAndDatabase = 'phone_number_and_database',
  PhoneNumberAndGovernmentIdAndSelfie = 'phone_number_and_government_id_and_selfie',
  Selfie = 'selfie',
  VehicleInsurance = 'vehicle_insurance',
}

export interface InquiryTemplateBuilders {
  'supported-builders': InquiryTemplateBuilder[];
}

export interface InquiryTemplateBuilder {
  name: BuilderName;
  'verification-types'?: VerificationTemplate['type'][];
  enabled: boolean;
}

export interface BuilderPreview {
  steps: BuilderPreviewSteps;
}

export type BuilderPreviewSteps = (StepPreview | CombinedStepType)[];
export type BuilderPreviewMap = Partial<Record<BuilderName, BuilderPreviewSteps>>;

export enum InquiryTemplateTier {
  Basic = 'basic',
  Enhanced = 'enhanced',
}

export interface InquiryTemplateAttributes {
  name: string;
  'name-display': string;
  description: string;
  'pricing-tier': InquiryTemplateTier;
  status: InquiryTemplateStatus;
  'verification-template-ids': string[];
  'created-at': string;
  'updated-at': string;
  'latest-published-at': string | undefined;
  'archived-at': string | undefined;
}

export enum InquiryTemplateStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export interface InquiryTemplateRelationships {
  'latest-published-version': ApiOptionalRelationship<ApiModel.InquiryTemplateVersion>;
  'latest-draft-version': ApiOptionalRelationship<ApiModel.InquiryTemplateVersion>;
}

export type InquiryTemplate = ApiResource<
  InquiryTemplateAttributes,
  InquiryTemplateRelationships,
  ApiModel.InquiryTemplate
>;

export interface InquiryTemplateFilterOption {
  id: string;
  type: ApiModel.InquiryTemplate;
  name: string;
  description?: string;
  status: InquiryTemplateStatus;
  'published-version-id': string | undefined;
  'updated-at'?: string;
}

export interface InquiryTemplateFiltersMeta {
  filters: InquiryTemplateFilterOption[];
}

export interface InquiryTemplateFiltersResponse {
  meta: InquiryTemplateFiltersMeta;
}
